import React, { useState, useRef, useEffect, createContext } from "react";
import { createPortal } from "react-dom";
import { MdArrowDropDown } from "react-icons/md";
import "./style.scss";

// Create a context to share the setIsOpen function
const DropdownContext = createContext(null);

export const Dropdown = ({
  children,
  trigger,
  placement = "bottom-start",
  hideDropdownIcon = false,
  triggerClassName = "",
  contentClassName = "",
  width = "auto",
  maxHeight = "300px",
  closeOnSelect = true,
  customTrigger = false,
  border,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const contentRef = useRef(null);
  const portalRef = useRef(null);

  // Create portal container on mount
  useEffect(() => {
    portalRef.current = document.createElement("div");
    portalRef.current.className = "dropdown-portal";
    document.body.appendChild(portalRef.current);

    return () => {
      if (portalRef.current) {
        document.body.removeChild(portalRef.current);
      }
    };
  }, []);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target) &&
        contentRef.current &&
        !contentRef.current.contains(event.target) &&
        !event.target.closest?.(".dropdown-portal")
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Position dropdown when opened
  useEffect(() => {
    const positionDropdown = () => {
      if (!isOpen || !triggerRef?.current || !contentRef?.current || !contentRef.current.style) return;

      const triggerRect = triggerRef.current.getBoundingClientRect();

      // Set width based on content if auto
      if (width === "auto") {
        // First set min-width to match trigger
        contentRef.current.style.minWidth = `${triggerRect.width}px`;

        // Then let it expand based on content
        contentRef.current.style.width = "auto";

        // Get all dropdown items and find the widest one
        const dropdownItems =
          contentRef.current.querySelectorAll(".dropdown-item");
        if (dropdownItems.length > 0) {
          let maxWidth = 0;
          dropdownItems.forEach((item) => {
            const itemWidth = item.scrollWidth + 32; // Add padding
            maxWidth = Math.max(maxWidth, itemWidth);
          });

          // Set width to the maximum of trigger width and content width
          if (maxWidth > triggerRect.width) {
            contentRef.current.style.width = `${maxWidth}px`;
          }
        }
      } else {
        const resolvedWidth =
          typeof width === "string" && width.endsWith("px")
            ? Math.min(parseInt(width, 10), window.innerWidth - 16)
            : null;
        if (resolvedWidth) {
          contentRef.current.style.width = `${resolvedWidth}px`;
        } else {
          contentRef.current.style.width = width;
        }
      }

      // Always position dropdown below the trigger (open downward)
      contentRef.current.style.top = `${triggerRect.bottom}px`;
      contentRef.current.style.bottom = "auto";
      contentRef.current.classList.remove("dropdown__content--top");

      const finalWidth =
        contentRef.current.offsetWidth ||
        contentRef.current.getBoundingClientRect().width;

      // Calculate horizontal position — keep fully inside the viewport
      if (placement.includes("end")) {
        const right = Math.max(8, window.innerWidth - triggerRect.right);
        const leftEdge = window.innerWidth - right - finalWidth;
        if (leftEdge < 8) {
          contentRef.current.style.left = "8px";
          contentRef.current.style.right = "auto";
        } else {
          contentRef.current.style.left = "auto";
          contentRef.current.style.right = `${right}px`;
        }
      } else if (placement.includes("center")) {
        const left =
          triggerRect.left + triggerRect.width / 2 - finalWidth / 2;
        contentRef.current.style.left = `${Math.max(8, Math.min(left, window.innerWidth - finalWidth - 8))}px`;
        contentRef.current.style.right = "auto";
      } else {
        // Default: start
        const left = Math.max(
          8,
          Math.min(triggerRect.left, window.innerWidth - finalWidth - 8),
        );
        contentRef.current.style.left = `${left}px`;
        contentRef.current.style.right = "auto";
      }
    };

    if (isOpen) {
      // Position immediately and then again after a short delay to account for content rendering
      positionDropdown();
      setTimeout(positionDropdown, 10);

      // Update position on scroll and resize
      window.addEventListener("resize", positionDropdown);
      window.addEventListener("scroll", positionDropdown, true); // Add capture phase to catch all scroll events
    }

    return () => {
      window.removeEventListener("resize", positionDropdown);
      window.removeEventListener("scroll", positionDropdown, true); // Remove with same options
    };
  }, [isOpen, placement, width]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <DropdownContext.Provider value={{ setIsOpen, closeOnSelect }}>
      <div
        className={`dropdown border ${className}`}
        ref={dropdownRef}
        style={{
          border: ` ${border}`,
        }}
      >
        <div
          ref={triggerRef}
          className={
            !customTrigger
              ? `dropdown__trigger ${
                  isOpen ? "dropdown__trigger--active" : ""
                } ${triggerClassName}`
              : `${
                  isOpen ? "dropdown__trigger--active" : ""
                } ${triggerClassName}`
          }
          onClick={handleToggle}
        >
          {trigger}
          {!hideDropdownIcon && (
            <MdArrowDropDown
              className={`dropdown__arrow ${
                isOpen ? "dropdown__arrow--open" : ""
              }`}
            />
          )}
        </div>

        {isOpen &&
          portalRef.current &&
          createPortal(
            <div
              ref={contentRef}
              className={`dropdown__content ${contentClassName}`}
              style={{
                position: "fixed",
                maxHeight: "none", // Remove fixed maxHeight from container
                width: "auto", // Start with auto width
                zIndex: 9999,
              }}
            >
              <div className="dropdown__content-inner">
                <div
                  className="dropdown__items-container"
                  style={{ maxHeight, overflowY: "auto" }}
                >
                  {children}
                </div>
              </div>
            </div>,
            portalRef.current,
          )}
      </div>
    </DropdownContext.Provider>
  );
};

export const DropdownContent = ({ children, className = "" }) => {
  return (
    <div className={`dropdown__content-inner ${className}`}>{children}</div>
  );
};

export const DropdownItem = ({
  children,
  onClick,
  active = false,
  closeOnSelect,
}) => {
  // Get access to the parent Dropdown's setIsOpen function
  const dropdownContext = React.useContext(DropdownContext);

  return (
    <div
      className={`dropdown-item ${active ? "active" : ""}`}
      onClick={(e) => {
        e.stopPropagation();
        if (onClick) onClick(e);
        // Close the dropdown after selection only if closeOnSelect is true
        // Use the prop value if provided, otherwise use the context value
        const shouldClose =
          closeOnSelect !== undefined
            ? closeOnSelect
            : dropdownContext?.closeOnSelect;
        if (shouldClose && dropdownContext?.setIsOpen) {
          dropdownContext.setIsOpen(false);
        }
      }}
    >
      {children}
    </div>
  );
};
